"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "@clerk/nextjs";
import { apiFetchAuthed } from "@/lib/api";

type FeatureFlagsContextType = {
  flags: Record<string, boolean>;
  loading: boolean;
  error: string | null;
  isEnabled: (flagKey: string) => boolean;
  refresh: () => Promise<void>;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  flags: {},
  loading: true,
  error: null,
  isEnabled: () => false,
  refresh: async () => {},
});

// Cache for feature flags to avoid refetching on navigation
let cachedFlags: Record<string, boolean> | null = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [flags, setFlags] = useState<Record<string, boolean>>(cachedFlags ?? {});
  const [loading, setLoading] = useState(!cachedFlags);
  const [error, setError] = useState<string | null>(null);

  const fetchFlags = useCallback(async () => {
    if (!isLoaded) return;

    // Check cache first
    if (cachedFlags && Date.now() - cacheTimestamp < CACHE_TTL_MS) {
      setFlags(cachedFlags);
      setLoading(false);
      return;
    }

    // Only fetch if signed in
    if (!isSignedIn) {
      setFlags({});
      setLoading(false);
      cachedFlags = {};
      cacheTimestamp = Date.now();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiFetchAuthed<{ flags: Record<string, boolean> }>(
        "feature-flags/my",
        {},
        getToken
      );
      const newFlags = response.flags ?? {};
      setFlags(newFlags);
      cachedFlags = newFlags;
      cacheTimestamp = Date.now();
    } catch (err) {
      console.error("Failed to fetch feature flags:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch feature flags");
      // Use empty flags on error
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const isEnabled = useCallback(
    (flagKey: string): boolean => {
      return flags[flagKey] === true;
    },
    [flags]
  );

  const refresh = useCallback(async () => {
    // Clear cache and refetch
    cachedFlags = null;
    cacheTimestamp = 0;
    await fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagsContext.Provider
      value={{
        flags,
        loading,
        error,
        isEnabled,
        refresh,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Hook to access feature flags
 * Returns an object with:
 * - flags: Record of all feature flags and their enabled state
 * - loading: Whether flags are still being fetched
 * - error: Error message if fetching failed
 * - isEnabled(flagKey): Check if a specific flag is enabled
 * - refresh(): Force refresh flags from server
 */
export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

/**
 * Hook to check if a specific feature flag is enabled
 * Returns { enabled, loading }
 */
export function useFeatureFlag(flagKey: string) {
  const { isEnabled, loading } = useFeatureFlags();
  return {
    enabled: isEnabled(flagKey),
    loading,
  };
}

// Feature flag keys as constants for type safety
export const FEATURE_FLAGS = {
  /** When true (or NEXT_PUBLIC_SKIP_NEW_ONBOARDING_FLOW), skip forced /onboarding redirects. */
  SKIP_NEW_ONBOARDING_FLOW: "skip_new_onboarding_flow",
  VENUE_MANAGEMENT: "venue_management",
  ADMIN_NOTIFICATIONS: "admin_notifications",
  EVENT_STAGES_ARTISTS: "event_stages_artists",
  ENHANCED_ONBOARDING: "enhanced_onboarding",
  VOUCHER_SYSTEM: "voucher_system",
  BROWSER_NOTIFICATIONS: "browser_notifications",
  PASSWORD_MANAGEMENT: "password_management",
  CUSTOM_FORMS: "custom_forms",
} as const;

export default useFeatureFlags;
